<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="transform opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition duration-200 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="transform opacity-0"
    >
      <div v-if="modelValue" class="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
        <!-- Fond sombre avec flou -->
        <div class="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm" @click="close"></div>

        <!-- Contenu du modal -->
        <div class="flex min-h-full items-center justify-center p-4">
          <div class="relative transform overflow-hidden rounded-lg bg-white shadow-xl transition-all w-full max-w-4xl max-h-[90vh] flex flex-col">
            <!-- En-tête avec indicateur de taille -->
            <div class="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <div class="flex items-center justify-between">
                <h3 class="text-lg font-semibold text-gray-900">
                  Sélectionner les sections à inclure dans le QR Code
                </h3>
                <button
                  class="text-gray-400 hover:text-gray-600 transition-colors"
                  @click="close"
                >
                  <XIcon class="h-6 w-6" />
                </button>
              </div>
              <!-- Indicateur de taille fixe -->
              <div class="mt-4 flex items-center gap-4">
                <div class="flex-1">
                  <div class="flex items-center justify-between text-sm mb-1">
                    <span class="text-gray-600">Taille du contenu</span>
                    <span
                      class="font-semibold"
                      :class="sizeStatus === 'ok' ? 'text-green-600' : 'text-red-600'"
                    >
                      {{ currentSize }} / {{ maxSize }} caractères
                    </span>
                  </div>
                  <div class="w-full bg-gray-200 rounded-full h-2">
                    <div
                      class="h-2 rounded-full transition-all duration-300"
                      :class="sizeStatus === 'ok' ? 'bg-green-500' : 'bg-red-500'"
                      :style="{ width: `${Math.min((currentSize / maxSize) * 100, 100)}%` }"
                    ></div>
                  </div>
                </div>
                <div
                  class="px-3 py-1 rounded-full text-xs font-medium"
                  :class="sizeStatus === 'ok' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'"
                >
                  {{ sizeStatus === 'ok' ? '✓ Taille OK' : '⚠ Trop volumineux' }}
                </div>
              </div>
            </div>

            <!-- Contenu scrollable -->
            <div class="flex-1 overflow-y-auto px-6 py-4">
              <div class="space-y-3">
                <div
                  v-for="section in sectionsTree"
                  :key="section.path"
                  class="border border-gray-200 rounded-lg p-4"
                >
                  <!-- Section principale -->
                  <div class="flex items-center gap-3 mb-2">
                    <input
                      :id="`section-${section.path}`"
                      type="checkbox"
                      :checked="section.checked"
                      class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      @change="toggleSection(section.path)"
                    />
                    <label
                      :for="`section-${section.path}`"
                      class="flex-1 font-semibold text-gray-900 cursor-pointer"
                    >
                      {{ section.label }}
                    </label>
                    <span class="text-xs text-gray-500">
                      {{ section.size }} caractères
                    </span>
                  </div>

                  <!-- Sous-sections -->
                  <div v-if="section.children && section.children.length > 0" class="ml-7 mt-2 space-y-2">
                    <div
                      v-for="subSection in section.children"
                      :key="subSection.path"
                      class="flex items-center gap-3 py-1"
                    >
                      <input
                        :id="`subsection-${subSection.path}`"
                        type="checkbox"
                        :checked="subSection.checked"
                        :disabled="!section.checked"
                        class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                        @change="toggleSubSection(section.path, subSection.path)"
                      />
                      <label
                        :for="`subsection-${subSection.path}`"
                        class="flex-1 text-sm text-gray-700 cursor-pointer"
                        :class="{ 'opacity-50 cursor-not-allowed': !section.checked }"
                      >
                        {{ subSection.label }}
                      </label>
                      <span class="text-xs text-gray-400">
                        {{ subSection.size }} caractères
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Pied de page -->
            <div class="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-end gap-3">
              <button
                type="button"
                class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                @click="close"
              >
                Annuler
              </button>
              <button
                type="button"
                class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                :disabled="sizeStatus !== 'ok'"
                @click="confirm"
              >
                Générer le QR Code
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { XIcon } from 'lucide-vue-next';
import { computed, ref, watch } from 'vue';

interface SectionNode {
  path: string;
  label: string;
  checked: boolean;
  size: number;
  children?: SectionNode[];
  data: any;
}

const props = defineProps<{
  modelValue: boolean;
  jsonData: Record<string, any>;
  maxSize?: number;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  'confirm': [filteredData: Record<string, any>];
}>();

const maxSize = computed(() => props.maxSize || 2953); // Limite QR code niveau L
const sectionsTree = ref<SectionNode[]>([]);
const currentSize = ref(0);

const sizeStatus = computed(() => {
  return currentSize.value <= maxSize.value ? 'ok' : 'too-large';
});

// Construire l'arbre de sections à partir du JSON
function buildSectionsTree(data: Record<string, any>, path = ''): SectionNode[] {
  const sections: SectionNode[] = [];

  for (const [key, value] of Object.entries(data)) {
    const currentPath = path ? `${path}.${key}` : key;
    
    if (value === null || value === undefined) {
      continue;
    }

    if (typeof value === 'object' && !Array.isArray(value)) {
      // C'est un objet, créer une section avec des sous-sections
      const children = buildSectionsTree(value, currentPath);
      const sectionSize = JSON.stringify(value).length;
      
      sections.push({
        path: currentPath,
        label: formatLabel(key),
        checked: true,
        size: sectionSize,
        children: children.length > 0 ? children : undefined,
        data: value
      });
    } else if (Array.isArray(value)) {
      // C'est un tableau, créer une section
      const sectionSize = JSON.stringify(value).length;
      sections.push({
        path: currentPath,
        label: formatLabel(key),
        checked: true,
        size: sectionSize,
        data: value
      });
    } else {
      // C'est une valeur primitive, créer une section (sera affichée comme sous-section si dans un objet)
      const sectionSize = JSON.stringify({ [key]: value }).length;
      sections.push({
        path: currentPath,
        label: formatLabel(key),
        checked: true,
        size: sectionSize,
        data: value
      });
    }
  }

  return sections;
}

// Formater le label pour l'affichage
function formatLabel(key: string): string {
  // Convertir camelCase en texte lisible
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim();
}

// Toggle une section entière
function toggleSection(path: string): void {
  const section = findSection(sectionsTree.value, path);
  if (!section) return;

  section.checked = !section.checked;
  
  // Décocher/cocher tous les enfants
  if (section.children) {
    section.children.forEach(child => {
      child.checked = section.checked;
    });
  }

  updateCurrentSize();
}

// Toggle une sous-section
function toggleSubSection(sectionPath: string, subSectionPath: string): void {
  const section = findSection(sectionsTree.value, sectionPath);
  if (!section || !section.children) return;

  const subSection = section.children.find(child => child.path === subSectionPath);
  if (!subSection) return;

  subSection.checked = !subSection.checked;
  
  // Si on décoche une sous-section, décocher la section parente
  // Si on coche toutes les sous-sections, cocher la section parente
  const allChecked = section.children.every(child => child.checked);
  section.checked = allChecked;

  updateCurrentSize();
}

// Trouver une section dans l'arbre
function findSection(sections: SectionNode[], path: string): SectionNode | null {
  for (const section of sections) {
    if (section.path === path) {
      return section;
    }
    if (section.children) {
      const found = findSection(section.children, path);
      if (found) return found;
    }
  }
  return null;
}

// Filtrer le JSON selon les sélections
function filterData(sections: SectionNode[]): Record<string, any> {
  const result: Record<string, any> = {};

  for (const section of sections) {
    if (!section.checked) continue;

    const pathParts = section.path.split('.');
    let current = result;
    
    // Naviguer jusqu'au parent
    for (let i = 0; i < pathParts.length - 1; i++) {
      if (!current[pathParts[i]]) {
        current[pathParts[i]] = {};
      }
      current = current[pathParts[i]];
    }

    const key = pathParts[pathParts.length - 1];

    // Si la section a des enfants, filtrer récursivement
    if (section.children && section.children.length > 0) {
      const hasCheckedChildren = section.children.some(child => child.checked);
      if (hasCheckedChildren) {
        // Reconstruire l'objet avec seulement les enfants cochés
        const filteredChildren: Record<string, any> = {};
        for (const child of section.children) {
          if (!child.checked) continue;
          
          if (child.children && child.children.length > 0) {
            const childFiltered = filterData([child]);
            Object.assign(filteredChildren, childFiltered);
          } else {
            // Enfant sans sous-enfants, extraire la clé du path
            const childKey = child.path.split('.').pop() || child.path;
            filteredChildren[childKey] = child.data;
          }
        }
        if (Object.keys(filteredChildren).length > 0) {
          current[key] = filteredChildren;
        }
      }
    } else {
      // Section sans enfants, copier la valeur directement
      current[key] = section.data;
    }
  }

  return result;
}

// Mettre à jour la taille actuelle
function updateCurrentSize(): void {
  const filteredData = filterData(sectionsTree.value);
  const jsonString = JSON.stringify(filteredData);
  currentSize.value = jsonString.length;
}

// Confirmer et générer le QR code
function confirm(): void {
  if (sizeStatus.value !== 'ok') return;
  
  const filteredData = filterData(sectionsTree.value);
  emit('confirm', filteredData);
  close();
}

// Fermer le modal
function close(): void {
  emit('update:modelValue', false);
}

// Initialiser l'arbre quand le JSON change
watch(() => props.jsonData, (newData) => {
  if (newData && Object.keys(newData).length > 0) {
    sectionsTree.value = buildSectionsTree(newData);
    updateCurrentSize();
  }
}, { immediate: true });

// Réinitialiser quand le modal s'ouvre
watch(() => props.modelValue, (isOpen) => {
  if (isOpen && props.jsonData) {
    sectionsTree.value = buildSectionsTree(props.jsonData);
    updateCurrentSize();
  }
});
</script>

